/**
 * Campaign Refinement Screen - Screen 2
 *
 * Conversational refinement interface for autonomous campaigns.
 *
 * Design Pattern: Split-screen chat (inspired by Metronic + KeenAI)
 * - Left Sidebar: Campaign context, progress, quick actions
 * - Main Chat Area: Q&A with ALICE
 *
 * Route: /campaigns/autonomous/:session_id/refine
 */

import { Rocket, Share2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CampaignVoiceInput } from '../components/metronic/campaign/CampaignVoiceInput';
import { InteractionModeSwitcher } from '../components/metronic/campaign/InteractionModeSwitcher';
import { KeenAIChatInput } from '../components/metronic/campaign/KeenAIChatInput';
import { KeenAIChatMessage } from '../components/metronic/campaign/KeenAIChatMessage';
import { KeenAISidebar } from '../components/metronic/campaign/KeenAISidebar';
import { LiveAvatarVideoPanel } from '../components/metronic/campaign/LiveAvatarVideoPanel';
import { Badge } from '../components/metronic/ui/Badge';
import { Button } from '../components/metronic/ui/Button';
import { API_BASE_URL } from '../config';

function CampaignRefinement({ previewMode = false }) {
    const { session_id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Preview mode: Show design without requiring session_id
    const isPreview = previewMode || !session_id;

    // Chat state
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [interactionMode, setInteractionMode] = useState('chat');

    // Campaign context
    const [campaignContext, setCampaignContext] = useState({
        jobTitle: '',
        goal: '',
        status: 'refining', // 'refining' | 'ready_to_execute'
        questionNumber: 0,
        totalQuestions: 0
    });

    // Session state
    const [conversationState, setConversationState] = useState('collecting_initial_info');

    // Refs
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);

    // Scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Load session data on mount
    useEffect(() => {
        const loadSession = async () => {
            try {
                // Fetch session data (we'll need to add this endpoint or use existing data)
                // For now, we'll initialize with the session_id from URL
                // The first message should come from the /start endpoint response

                // Check if we have initial question from navigation state
                // Otherwise, we'll get it from the session
                setCampaignContext(prev => ({
                    ...prev,
                    status: 'refining'
                }));
            } catch (error) {
                console.error('Error loading session:', error);
            }
        };

        if (session_id) {
            loadSession();
        }
    }, [session_id]);

    // Send message to ALICE
    const handleSendMessage = async (message) => {
        if (!message.trim() || isProcessing) return;

        // Preview mode: Just add messages without API call
        if (isPreview) {
            const userMessage = {
                text: message,
                sender: 'user',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, userMessage]);
            setInput('');

            const previewResponse = "Great! I've noted that. Let me ask another question: What are your preferred contact channels? (Email, WhatsApp, Call, or all of them?)";

            // Simulate ALICE response
            setTimeout(() => {
                const aliceResponse = {
                    text: previewResponse,
                    sender: 'alice',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };
                setMessages(prev => [...prev, aliceResponse]);
                setCampaignContext(prev => ({
                    ...prev,
                    questionNumber: prev.questionNumber + 1
                }));
            }, 1000);
            return { status: 'refining', aliceText: previewResponse };
        }

        // Add user message to chat
        const userMessage = {
            text: message,
            sender: 'user',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsProcessing(true);
        setIsTyping(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${session_id}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: message,
                    update_form_data: {} // Can be used to update form data during conversation
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to send message');
            }

            // Handle response based on status
            if (data.status === 'ready_to_execute') {
                // Refinement complete - navigate to execution plan review
                setCampaignContext(prev => ({
                    ...prev,
                    status: 'ready_to_execute'
                }));

                // Add summary message
                const summaryMessage = {
                    text: data.conversation_summary || 'Great! I have all the information I need. Let me create an execution plan for you.',
                    sender: 'alice',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };

                setMessages(prev => [...prev, summaryMessage]);

                // Store refined goal for plan review screen
                if (data.refined_goal) {
                    localStorage.setItem(`refined_goal_${session_id}`, JSON.stringify(data.refined_goal));
                }

                // Auto-navigate after a short delay
                setTimeout(() => {
                    navigate(`/campaigns/autonomous/${session_id}/plan`);
                }, 2000);

                return { status: data.status, aliceText: summaryMessage.text, data };

            } else if (data.status === 'refining') {
                // Continue refinement - add ALICE's question
                const aliceMessage = {
                    text: data.conversation_context?.message || 'Processing your response...',
                    sender: 'alice',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };

                setMessages(prev => [...prev, aliceMessage]);
                setConversationState(data.conversation_context?.state || 'refining');

                // Update question counter if available
                if (data.conversation_context) {
                    setCampaignContext(prev => ({
                        ...prev,
                        questionNumber: prev.questionNumber + 1
                    }));
                }

                return { status: data.status, aliceText: aliceMessage.text, data };
            }

            return { status: data.status || 'refining', aliceText: data.message || '', data };

        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                text: `Error: ${error.message}. Please try again.`,
                sender: 'system',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, errorMessage]);
            return { status: 'error', aliceText: errorMessage.text, error };
        } finally {
            setIsProcessing(false);
            setIsTyping(false);
        }
    };

    // Quick action handlers
    const handleSkipQuestions = async () => {
        await handleSendMessage('Skip remaining questions');
    };

    const handleContinue = async () => {
        await handleSendMessage('Continue');
    };

    // Load initial question function
    const loadInitialQuestion = useCallback(async () => {
        try {
            // Prefer question data passed via route state (most up to date)
            const stateQuestion = location.state?.questionData;
            let questionData = stateQuestion || null;

            // Fallback: Check localStorage for initial question (set by AutonomousCampaign after /start)
            if (!questionData) {
                const storedQuestionRaw = localStorage.getItem(`campaign_question_${session_id}`);
                if (storedQuestionRaw) {
                    questionData = JSON.parse(storedQuestionRaw);
                    // Clear it so we don't reuse stale data across sessions
                    localStorage.removeItem(`campaign_question_${session_id}`);
                }
            }

            if (questionData) {

                // Update campaign context from stored job and goal
                setCampaignContext(prev => ({
                    ...prev,
                    jobTitle: questionData.jobTitle || '',
                    goal: questionData.goal || '',
                    questionNumber: 1,
                    totalQuestions: prev.totalQuestions || 5,
                }));

                const jobTitle = questionData.jobTitle;
                const goal = questionData.goal;

                // Contextual intro that explicitly references the job and goal
                let introText = "Hi! I'm ALICE. I've reviewed your campaign details.";
                if (jobTitle || goal) {
                    introText = `Hi! I'm ALICE. I've reviewed your campaign for ${jobTitle || 'this role'}${goal ? ` with goal: ${goal}` : ''}.`;
                }

                const welcomeMessage = {
                    text: `${introText} Let me ask a few questions to refine your requirements and create the best execution plan.`,
                    sender: 'alice',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };

                // If backend provided an initial question, show it as the second message
                const initialQuestionText =
                    questionData.message ||
                    'First, could you clarify any must-have requirements versus nice-to-have for this role?';

                const questionMessage = {
                    text: initialQuestionText,
                    sender: 'alice',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };

                setMessages([welcomeMessage, questionMessage]);
            } else {
                const welcomeMessage = {
                    text: "Hi! I'm ALICE. I've reviewed your campaign details. Let me ask a few questions to refine your requirements and create the best execution plan.",
                    sender: 'alice',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };
                setMessages([welcomeMessage]);
            }
        } catch (error) {
            console.error('Error loading initial question:', error);
            const welcomeMessage = {
                text: "Hi! I'm ALICE. I've reviewed your campaign details. Let me ask a few questions to refine your requirements.",
                sender: 'alice',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages([welcomeMessage]);
        }
    }, [session_id, location.state]);

    // Load initial question on mount
    useEffect(() => {
        // Preview mode: Show sample data
        if (isPreview) {
            const welcomeMessage = {
                text: "Hi! I'm ALICE. I've reviewed your campaign details. Let me ask a few questions to refine your requirements and create the best execution plan.",
                sender: 'alice',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            const sampleQuestion = {
                text: "What's the minimum evaluation score you'd like for candidates? (e.g., 70, 80, 90)",
                sender: 'alice',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages([welcomeMessage, sampleQuestion]);
            setCampaignContext(prev => ({
                ...prev,
                jobTitle: 'Senior Software Engineer',
                goal: 'Find 10 candidates with score above 70',
                questionNumber: 1,
                totalQuestions: 5
            }));
            return;
        }

        if (session_id && messages.length === 0) {
            loadInitialQuestion();
        }
    }, [session_id, messages.length, isPreview, loadInitialQuestion]);

    const handleNewChat = () => {
        setMessages([]);
        setInput('');
        setIsProcessing(false);
        setIsTyping(false);
        if (session_id && !isPreview) {
            loadInitialQuestion();
        }
    };

    const handleModelSelect = (modelId) => {
        console.log('Selected model:', modelId);
    };

    return (
        <div className="flex h-screen bg-white overflow-hidden">
            {/* KeenAI-Style Sidebar */}
            <KeenAISidebar
                campaignContext={campaignContext}
                onNewChat={handleNewChat}
            />

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col ml-80 overflow-hidden">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <h1 className="text-lg font-semibold text-gray-900">
                                {interactionMode === 'video' ? 'Video' : interactionMode === 'voice' ? 'Voice' : 'Chat'}
                            </h1>
                            <InteractionModeSwitcher
                                value={interactionMode}
                                onChange={setInteractionMode}
                                disabled={isProcessing}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                            >
                                Back to Dashboard
                            </button>
                            <Badge
                                variant={campaignContext.status === 'ready_to_execute' ? 'success' : 'warning'}
                                size="sm"
                            >
                                {campaignContext.status === 'ready_to_execute' ? 'Ready' : 'Refining'}
                            </Badge>
                            <Button
                                variant="outline"
                                size="sm"
                                className="bg-black text-white border-black hover:bg-gray-800 h-9 px-4"
                            >
                                <Share2 className="w-4 h-4 mr-2" />
                                Share Chat
                            </Button>
                        </div>
                    </div>
                </div>

                {interactionMode === 'video' ? (
                    <LiveAvatarVideoPanel
                        sessionId={session_id}
                        messages={messages}
                        onSubmit={handleSendMessage}
                        disabled={isProcessing}
                        previewMode={isPreview}
                    />
                ) : (
                    <>
                        {/* Chat Messages Area */}
                        <div className="flex-1 overflow-y-auto px-6 py-6 bg-white">
                            <div ref={chatContainerRef} className="max-w-4xl mx-auto">
                                {messages.length > 0 ? (
                                    messages.map((message, index) => (
                                        <KeenAIChatMessage
                                            key={index}
                                            message={message.text}
                                            sender={message.sender === 'alice' || message.sender === 'system' ? 'ai' : 'user'}
                                            timestamp={message.timestamp}
                                            avatar={message.sender === 'user' ? undefined : undefined}
                                        />
                                    ))
                                ) : (
                                    <div className="text-center text-gray-500 py-12">
                                        <Rocket className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                                        <p className="text-lg font-medium mb-2">Starting Refinement</p>
                                        <p className="text-sm">ALICE will ask questions to refine your campaign requirements</p>
                                    </div>
                                )}

                                {/* Typing Indicator */}
                                {isTyping && (
                                    <KeenAIChatMessage
                                        message=""
                                        sender="ai"
                                        timestamp=""
                                        isTyping={true}
                                    />
                                )}

                                <div ref={messagesEndRef} />
                            </div>
                        </div>

                        {interactionMode === 'voice' && (
                            <CampaignVoiceInput
                                onSubmit={handleSendMessage}
                                disabled={isProcessing}
                                placeholder="Say your answer for ALICE..."
                            />
                        )}

                        {/* KeenAI-Style Input */}
                        <KeenAIChatInput
                            value={input}
                            onChange={setInput}
                            onSend={handleSendMessage}
                            placeholder={interactionMode === 'voice' ? 'Type instead...' : 'Message...'}
                            disabled={isProcessing}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default CampaignRefinement;

